import React from 'react'
import PropTypes from 'prop-types'
import { createPopper } from '@popperjs/core'
import { Portal } from '../'
import { useForkRef, setRef } from 'hooks'
import { createChainedFunction } from 'settings/app/helpers'
import { HTMLElementType, refType } from 'settings/propTypeDefinitions'
import { __dev__ } from 'settings/app/constants'

function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl
}

const useEnchancedEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

const defaultPopperOptions = {
  modifiers: [],
}

const Popper = React.forwardRef((props, ref) => {
  const {
    anchorEl,
    children,
    className,
    container,
    disablePortal = false,
    keepMounted = false,
    modifiers = [],
    open,
    placement: initialPlacement = 'bottom',
    popperOptions = defaultPopperOptions,
    popperRef: popperRefProp,
    style,
    transition = false,
    ...other
  } = props

  const tooltipRef = React.useRef(null)
  const ownRef = useForkRef(tooltipRef, ref)

  const popperRef = React.useRef(null)
  const handlePopperRef = useForkRef(popperRef, popperRefProp)
  const handlePopperRefRef = React.useRef(handlePopperRef)
  useEnchancedEffect(() => {
    handlePopperRefRef.current = handlePopperRef
  }, [handlePopperRef])
  React.useEffect(() => {
    handlePopperRefRef.current = handlePopperRef
  }, [handlePopperRef])
  React.useImperativeHandle(popperRefProp, () => popperRef.current, [])

  const [exited, setExited] = React.useState(true)

  const [placement, setPlacement] = React.useState(initialPlacement)

  React.useEffect(() => {
    if (popperRef.current) {
      popperRef.current.update()
    }
  })

  const handleOpen = React.useCallback(() => {
    if (!tooltipRef.current || !anchorEl || !open) {
      return
    }

    if (popperRef.current) {
      popperRef.current.destroy()
      handlePopperRefRef.current(null)
    }

    const handlePopperUpdate = (data) => {
      setPlacement(data.placement)
    }

    const resolvedAnchorEl = getAnchorEl(anchorEl)

    if (__dev__) {
      if (resolvedAnchorEl && resolvedAnchorEl.nodeType === 1) {
        const box = resolvedAnchorEl.getBoundingClientRect()

        if (
          process.env.NODE_ENV !== 'test' &&
          box.top === 0 &&
          box.left === 0 &&
          box.right === 0 &&
          box.bottom === 0
        ) {
          console.warn(
            [
              'The `anchorEl` prop provided to the component is invalid.',
              'The anchor element should be part of the document layout.',
              `Make sure the element is present in the document or that it's not display none.`,
            ].join('\n')
          )
        }
      }
    }

    const popper = createPopper(getAnchorEl(anchorEl), tooltipRef.current, {
      placement: initialPlacement,
      ...popperOptions,
      modifiers: [
        ...[
          disablePortal && {
            // It's using scrollParent by default, we can use the viewport when using a portal
            name: 'preventOverflow',
            options: {
              rootBoundary: 'window',
            },
          },
        ],
        ...modifiers,
        ...popperOptions.modifiers,
      ],
      onCreate: createChainedFunction(
        handlePopperUpdate,
        popperOptions.onCreate
      ),
      onUpdate: createChainedFunction(
        handlePopperUpdate,
        popperOptions.onUpdate
      ),
    })

    handlePopperRefRef.current(popper)
  }, [
    anchorEl,
    disablePortal,
    modifiers,
    open,
    initialPlacement,
    popperOptions,
  ])

  const handleRef = React.useCallback(
    (node) => {
      setRef(ownRef, node)
      handleOpen()
    },
    [ownRef, handleOpen]
  )

  const handleEnter = () => {
    setExited(false)
  }

  const handleClose = () => {
    if (!popperRef.current) {
      return
    }

    popperRef.current.destroy()
    handlePopperRefRef.current(null)
  }

  const handleExited = () => {
    setExited(true)
    handleClose()
  }

  React.useEffect(
    () => () => {
      handleClose()
    },
    []
  )

  React.useEffect(() => {
    if (!open && !transition) {
      // otherwise handleExited will call this
      handleClose()
    }
  }, [open, transition])

  if (!keepMounted && !open && (!transition || exited)) {
    return null
  }

  const childProps = { placement }

  if (transition) {
    childProps.TransitionProps = {
      in: open,
      onEnter: handleEnter,
      onExited: handleExited,
    }
  }

  return (
    <Portal disablePortal={ disablePortal } container={ container }>
      <div
        ref={ handleRef }
        role='tooltip'
        { ...other }
        style={{
          // Prevents scroll issue
          position: 'fixed',
          // Fix Popper.js display issue
          top: 0,
          left: 0,
          display: !open && keepMounted && !transition ? 'none' : null,
          ...style,
        }}
        className={ className }>
        {typeof children === 'function' ? children(childProps) : children}
      </div>
    </Portal>
  )
})

Popper.propTypes = {
  // Popper render function or node
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * A HTML element or function that returns one.
   * The `container` will have the portal children appended to it.
   *
   * By default, it uses the body of the top-level document object,
   * so it's simply `document.body` mot of the time.
   */
  container: PropTypes.oneOfType([HTMLElementType, PropTypes.func]),
  // The `children` will be inside the DOM hierachy of the parent component.
  disablePortal: PropTypes.bool,
  /**
   * Always keep the children in the DOM.
   * This prop can be useful in SEO situation or
   * when you want to maximaize the responsiveness of the Popper.
   */
  keepMounted: PropTypes.bool,
  /**
   * Popper.js is based on a "plugin-like" architecture,
   * most of its features are fully encapsulated "modifiers".
   *
   * A modifier is a function that is called each time Popper.js needs to
   * compute the position of the popper.
   * For this reason, modifiers should be very performant to avoid bottlenecks.
   */
  modifiers: PropTypes.array,
  // If `true`, the popper is visible
  open: PropTypes.bool.isRequired,
  // Popper placement
  placement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  // Options provided to the popper instance
  popperOptions: PropTypes.object,
  // A ref that points to he used popper instance
  popperRef: refType,
  style: PropTypes.object,
  // Help supporting a react-transition-group/Transition component
  transition: PropTypes.bool,
}

export default Popper
